import {Mapper} from '@s-ui/domain'

export default class FromRawDataToAdvertisingValueObjectMapper extends Mapper {
  constructor({config, valueObjectFactory}) {
    super({config, valueObjectFactory})

    this._config = config
    this._adsNamePageMap = this._config.get('advertising').ADS_NAME_PAGE_MAP
    this._adsListSubscriptionPageName = this._config.get('advertising').ADS_NAME_PAGE_SUBSCRIPTION_DEFAULT
    this._adsNamePageAdsListBase = this._config.get('advertising').ADS_NAME_PAGE_ADS_LIST_BASE
    this._categoriesLevel1 = this._config.get('advertising').CATEGORIES_LEVEL_1
    this._categoryLevelRenting = this._config.get('advertising').CATEGORY_LEVEL_RENTING
    this._vehicleStatus = this._config.get('advertising').VEHICLE_STATUS
    this._vehicleStatusRenting = this._config.get('advertising').VEHICLE_STATUS_RENTING
    this._fuelTypes = this._config.get('advertising').FUEL_TYPES
    this._culture = this._config.get('DEFAULT_CULTURE')
    this._lang = this._config.get('URL_CULTURES_MAP')[this._culture]
    this._valueObjectFactory = valueObjectFactory

    this._pageConfig = this._config.get('page')
    this._defaultProvince = this._config.get('search').DEFAULT_PROVINCE
    this._defaultFuelType = this._config.get('search').DEFAULT_FUEL_TYPE
    this._defaultMake = this._config.get('advertising').DEFAULT_MAKE
    this._defaultBodyType = this._config.get('search').DEFAULT_BODY_TYPE
    this._defaultPrice = null
    this._defaultYear = null
    this._defaultKms = null
    this._defaultPower = null
  }

  _getDataFromVehicles = ({vehicles}) => {
    return vehicles.reduce(
      (acc, vehicle) => {
        acc.brands = vehicle.make ? [...acc.brands, vehicle.make] : acc.brands
        acc.brandIds = vehicle.makeId ? [...acc.brandIds, vehicle.makeId] : acc.brandIds
        acc.modelIds = vehicle.model?.id ? [...acc.modelIds, vehicle.model?.id] : acc.modelIds
        acc.versions = vehicle.version ? [...acc.versions, vehicle.version] : acc.versions
        return acc
      },
      {
        brands: [],
        brandIds: [],
        modelIds: [],
        versions: []
      }
    )
  }

  _isSubscriptionPage = () =>
    [
      this._pageConfig.ADS_LIST_SUBSCRIPTION_PAGE_NAME,
      this._pageConfig.ADS_LIST_BUSINESS_SUBSCRIPTION_PAGE_NAME
    ].includes(this._pageName)

  _getCategoryText = categoryId => {
    if (categoryId) return this._categoriesLevel1[categoryId][this._culture]
    if (this._isSubscriptionPage()) return this._categoryLevelRenting[this._culture]
    return ''
  }

  _getVehicleStatus = offerTypeId => {
    const vehicleStatusLiteral = this._vehicleStatus[offerTypeId]
    const vehicleStatusRentingLiteral = this._vehicleStatusRenting
    if (vehicleStatusLiteral) return vehicleStatusLiteral[this._culture]
    if (this._isSubscriptionPage()) return vehicleStatusRentingLiteral[this._culture]
    return ''
  }

  _getFuelText = fuelTypeId => {
    const fuelTypeLiteral = this._fuelTypes[fuelTypeId]
    return fuelTypeLiteral ? fuelTypeLiteral[this._culture] : ''
  }

  _getAdsNamePage = ({categoryId, offerTypeId}) => {
    return this._isSubscriptionPage()
      ? `${this._adsListSubscriptionPageName}/${this._adsNamePageAdsListBase}`
      : `${this._adsNamePageMap[categoryId]?.[offerTypeId]}/${this._adsNamePageAdsListBase}`
  }

  _getOfferTypeId = (filteredOfferTypeId, offerTypeId) => {
    const {OFFER_TYPE_SECOND_HAND, OFFER_TYPE_NEW, OFFER_TYPE_KM0, UNSELECTED_OFFER_TYPE} = this._config.get('search')

    const id =
      filteredOfferTypeId != null && filteredOfferTypeId !== UNSELECTED_OFFER_TYPE ? filteredOfferTypeId : offerTypeId
    return [OFFER_TYPE_SECOND_HAND, OFFER_TYPE_NEW, OFFER_TYPE_KM0].includes(id) ? {offer_type: id} : {}
  }

  setParams = ({pageName} = {}) => {
    this._pageName = pageName
    return this
  }

  map = ({search} = {}) => {
    const {
      bodyTypeIds = [],
      categoryId = null,
      kms: {from: kmMin, to: kmMax} = {},
      fuelTypeIds = [],
      hp: {from: hpMin, to: hpMax} = {},
      litres: {from: litresMin, to: litresMax} = {},
      offerTypeId = null,
      price: {from: priceMin, to: priceMax} = {},
      provinceIds = [],
      vehicles = [],
      year: {from: yearMin, to: yearMax} = {},
      filteredOfferTypeId = null
    } = search

    const {brands, brandIds, modelIds, versions} = this._getDataFromVehicles({
      vehicles
    })

    return this._valueObjectFactory({
      event_name: 'list',
      ads_name_page: this._getAdsNamePage({categoryId, offerTypeId}),
      country_code: this._lang,
      brand: brands.length ? brands : '',
      brand_id: brandIds.length ? brandIds : 0,
      model_id: modelIds.length ? modelIds : 0,
      version: versions.length ? versions : '',
      km_min: kmMin || this._defaultKms,
      km_max: kmMax || this._defaultKms,
      price_min: priceMin || this._defaultPrice,
      price_max: priceMax || this._defaultPrice,
      year_min: yearMin || this._defaultYear,
      year_max: yearMax || this._defaultYear,
      power_min: (hpMin ?? litresMin) || this._defaultPower,
      power_max: (hpMax ?? litresMax) || this._defaultPower,
      region_level2_id: provinceIds.length ? provinceIds : this._defaultProvince,
      fuel: this._getFuelText(fuelTypeIds[0]),
      fuel_id: fuelTypeIds[0] || this._defaultFuelType,
      car_body_id: bodyTypeIds[0] || this._defaultBodyType,
      vehicle_status: this._getVehicleStatus(offerTypeId),
      vehicle_status_id: offerTypeId,
      warranty: false,
      category_level1: this._getCategoryText(categoryId),
      category_level1_id: categoryId,
      site: this._config.get('TENANT'),
      ...this._getOfferTypeId(filteredOfferTypeId, offerTypeId)
    })
  }
}
